import { useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Layers3, ClipboardCheck, CalendarDays, Wallet, ShieldCheck, Check, X, Clock, type LucideIcon } from "lucide-react";
import { T } from "./tokens";
import { Avatar, Bar, Stat, Tabs, fmt } from "./DemoParts";

const BRANCHES = [
    { key: "baku", students: 412, groups: 28, revenue: 18450, load: 86 },
    { key: "ganja", students: 186, groups: 12, revenue: 7920, load: 71 },
    { key: "sumgait", students: 143, groups: 9, revenue: 6105, load: 64 },
] as const;

function BranchesDemo() {
    const { t, i18n } = useTranslation();
    const [k, setK] = useState<(typeof BRANCHES)[number]["key"]>("baku");
    const b = BRANCHES.find((x) => x.key === k)!;
    return (
        <div className="space-y-4">
            <Tabs testid="demo-branches-tabs" value={k} onChange={setK} items={BRANCHES.map((x) => ({ key: x.key, label: t(`landing.demo.city.${x.key}` as any) }))} />
            <div className="grid grid-cols-3 gap-2" key={k}>
                <Stat label={t("landing.demo.lbl.students")} value={fmt(b.students, i18n.language)} testid="demo-branches-students" />
                <Stat label={t("landing.demo.lbl.groups")} value={b.groups} />
                <Stat label={t("landing.demo.lbl.revenue")} value={`${fmt(b.revenue, i18n.language)} ₼`} tone="success" />
            </div>
            <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: T.border, background: T.card }}>
                <div className="flex justify-between text-xs"><span style={{ color: T.textMuted }}>{t("landing.demo.lbl.room_load")}</span><span className="font-semibold tabular-nums" style={{ color: T.text }}>{b.load}%</span></div>
                <Bar pct={b.load} />
            </div>
        </div>
    );
}

const GROUPS = [
    { id: "ielts", name: "IELTS B2", teacher: "Leyla Həsənova", cur: 11, cap: 12, roster: ["Aysel Məmmədova", "Murad Əliyev", "Nərmin Quliyeva", "Elvin Rzayev"] },
    { id: "math", name: "Math 9A", teacher: "Orxan Qasımov", cur: 8, cap: 15, roster: ["Fidan Kərimova", "Tural Abbasov", "Səbinə İsmayılova"] },
    { id: "robo", name: "Robotics Kids", teacher: "Nigar Axundova", cur: 14, cap: 14, roster: ["Kamran Hüseynov", "Lalə Novruzova", "Rauf Cəfərov", "Ülviyyə Babayeva"] },
];

function GroupsDemo() {
    const { t } = useTranslation();
    const [id, setId] = useState(GROUPS[0].id);
    const g = GROUPS.find((x) => x.id === id)!;
    return (
        <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
                {GROUPS.map((x) => (
                    <button key={x.id} type="button" onClick={() => setId(x.id)} data-testid={`demo-group-${x.id}`}
                        className="w-full text-left rounded-xl border px-3 py-2.5 transition-[border-color,background-color] duration-200"
                        style={{ borderColor: x.id === id ? T.primary : T.border, background: x.id === id ? T.primarySoft : T.card }}>
                        <div className="text-sm font-medium" style={{ color: T.text }}>{x.name}</div>
                        <div className="text-[11px]" style={{ color: T.textMuted }}>{x.cur}/{x.cap} · {x.cur >= x.cap ? t("landing.demo.groups.full") : t("landing.demo.groups.open")}</div>
                    </button>
                ))}
            </div>
            <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: T.border, background: T.card }} data-testid="demo-group-detail">
                <div className="text-[11px] uppercase tracking-wider" style={{ color: T.textMuted }}>{t("landing.demo.lbl.teacher")}</div>
                <div className="flex items-center gap-2 text-sm" style={{ color: T.text }}><Avatar name={g.teacher} />{g.teacher}</div>
                <div className="flex justify-between text-xs"><span style={{ color: T.textMuted }}>{t("landing.demo.lbl.capacity")}</span><span className="tabular-nums" style={{ color: T.text }}>{g.cur}/{g.cap}</span></div>
                <Bar pct={(g.cur / g.cap) * 100} color={g.cur >= g.cap ? T.warning : T.primary} />
                <div className="flex -space-x-1">{g.roster.map((n) => <span key={n} title={n} className="ring-2 ring-white rounded-full"><Avatar name={n} /></span>)}</div>
            </div>
        </div>
    );
}

type Att = "present" | "late" | "absent";
const NEXT: Record<Att, Att> = { present: "late", late: "absent", absent: "present" };
const ATT_STYLE: Record<Att, { bg: string; fg: string; Icon: LucideIcon }> = {
    present: { bg: T.successSoft, fg: T.success, Icon: Check },
    late: { bg: T.warningSoft, fg: T.warning, Icon: Clock },
    absent: { bg: T.dangerSoft, fg: T.danger, Icon: X },
};
const STUDENTS = ["Aysel Məmmədova", "Murad Əliyev", "Nərmin Quliyeva", "Elvin Rzayev", "Fidan Kərimova"];

function AttendanceDemo() {
    const { t } = useTranslation();
    const [st, setSt] = useState<Att[]>(["present", "present", "late", "absent", "present"]);
    const rate = Math.round((st.filter((s) => s !== "absent").length / st.length) * 100);
    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
                <div className="text-xs" style={{ color: T.textMuted }}>{t("landing.demo.attendance.hint")}</div>
                <button type="button" data-testid="demo-att-all" onClick={() => setSt(st.map(() => "present"))} className="shrink-0 rounded-lg px-2.5 py-1 text-xs font-medium border transition-colors hover:bg-violet-50" style={{ borderColor: T.accent, color: T.primary }}>{t("landing.demo.attendance.mark_all")}</button>
            </div>
            <div className="rounded-xl border divide-y divide-slate-200" style={{ borderColor: T.border, background: T.card }}>
                {STUDENTS.map((n, i) => {
                    const s = ATT_STYLE[st[i]];
                    return (
                        <div key={n} className="flex items-center gap-2 px-3 py-2">
                            <Avatar name={n} /><span className="text-sm flex-1 truncate" style={{ color: T.text }}>{n}</span>
                            <button type="button" data-testid={`demo-att-${i}`} onClick={() => setSt(st.map((v, j) => (j === i ? NEXT[v] : v)))}
                                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-transform duration-150 active:scale-95"
                                style={{ background: s.bg, color: s.fg }}>
                                <s.Icon size={12} />{t(`landing.demo.attendance.${st[i]}` as any)}
                            </button>
                        </div>
                    );
                })}
            </div>
            <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: T.border, background: T.card }}>
                <div className="flex justify-between text-xs"><span style={{ color: T.textMuted }}>{t("landing.demo.attendance.rate")}</span><span className="font-semibold tabular-nums" style={{ color: T.text }} data-testid="demo-att-rate">{rate}%</span></div>
                <Bar pct={rate} color={rate >= 80 ? T.success : T.warning} />
            </div>
        </div>
    );
}

const SESSIONS: Record<number, { time: string; group: string; room: number }[]> = {
    0: [{ time: "09:00–10:30", group: "IELTS B2", room: 2 }, { time: "15:00–16:30", group: "Math 9A", room: 1 }],
    1: [{ time: "10:00–11:00", group: "Robotics Kids", room: 3 }],
    2: [{ time: "09:00–10:30", group: "IELTS B2", room: 2 }, { time: "11:00–12:00", group: "Math 9A", room: 1 }, { time: "17:00–18:30", group: "Robotics Kids", room: 3 }],
    3: [{ time: "14:00–15:30", group: "Math 9A", room: 1 }],
    4: [{ time: "09:00–10:30", group: "IELTS B2", room: 2 }, { time: "16:00–17:00", group: "Robotics Kids", room: 3 }],
    5: [{ time: "10:00–12:00", group: "Robotics Kids", room: 3 }],
};

function ScheduleDemo() {
    const { t } = useTranslation();
    const [d, setD] = useState("2");
    const list = SESSIONS[Number(d)];
    return (
        <div className="space-y-3">
            <Tabs testid="demo-sched-tabs" value={d} onChange={setD} items={[0, 1, 2, 3, 4, 5].map((w) => ({ key: String(w), label: t(`weekday.${w}` as any) }))} />
            <div className="space-y-2" key={d}>
                {list.map((s) => (
                    <div key={s.time} className="flex items-center gap-3 rounded-xl border px-3 py-2.5 animate-in fade-in-0 slide-in-from-bottom-1 duration-300" style={{ borderColor: T.border, background: T.card, borderLeft: `3px solid ${T.primary}` }}>
                        <span className="text-xs font-semibold tabular-nums w-24 shrink-0" style={{ color: T.primary }}>{s.time}</span>
                        <span className="text-sm flex-1 truncate" style={{ color: T.text }}>{s.group}</span>
                        <span className="text-[11px] rounded-md px-2 py-0.5" style={{ background: T.page, color: T.textMuted }}>{t("landing.demo.lbl.room", { n: s.room })}</span>
                    </div>
                ))}
            </div>
            <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium" style={{ background: T.successSoft, color: T.success }} data-testid="demo-sched-ok"><Check size={12} />{t("landing.demo.schedule.no_conflicts")}</div>
        </div>
    );
}

const MONTHS = [62, 74, 58, 81, 77, 92];
const PAYMENTS = [{ n: "Aysel Məmmədova", a: 180, d: "03.10" }, { n: "Murad Əliyev", a: 240, d: "02.10" }, { n: "Fidan Kərimova", a: 150, d: "01.10" }];
const DEBTS = [{ n: "Elvin Rzayev", a: 120, days: 12 }, { n: "Tural Abbasov", a: 90, days: 5 }];

function FinanceDemo() {
    const { t, i18n } = useTranslation();
    const [tab, setTab] = useState<"payments" | "debts">("payments");
    return (
        <div className="space-y-3">
            <div className="rounded-xl border p-3" style={{ borderColor: T.border, background: T.card }}>
                <div className="flex items-end justify-between mb-2">
                    <div><div className="text-[11px] uppercase tracking-wider" style={{ color: T.textMuted }}>{t("landing.demo.finance.collected")}</div><div className="text-xl font-semibold tabular-nums" style={{ color: T.text }}>{fmt(12340, i18n.language)} ₼</div></div>
                    <span className="text-[11px] font-medium rounded-full px-2 py-0.5" style={{ background: T.successSoft, color: T.success }}>+19%</span>
                </div>
                <div className="flex items-end gap-1.5 h-16">
                    {MONTHS.map((h, i) => <div key={i} className="flex-1 rounded-t-md transition-[height] duration-500" style={{ height: `${h}%`, background: i === MONTHS.length - 1 ? T.primary : T.accent }} />)}
                </div>
            </div>
            <Tabs testid="demo-fin-tabs" value={tab} onChange={setTab} items={[{ key: "payments", label: t("landing.demo.finance.payments") }, { key: "debts", label: t("landing.demo.finance.debts") }]} />
            <div className="rounded-xl border divide-y divide-slate-200" style={{ borderColor: T.border, background: T.card }} key={tab}>
                {tab === "payments" ? PAYMENTS.map((p) => (
                    <div key={p.n} className="flex items-center gap-2 px-3 py-2 text-sm"><Avatar name={p.n} /><span className="flex-1 truncate" style={{ color: T.text }}>{p.n}</span><span className="text-[11px]" style={{ color: T.textMuted }}>{p.d}</span><span className="font-medium tabular-nums" style={{ color: T.success }}>+{p.a} ₼</span></div>
                )) : DEBTS.map((p) => (
                    <div key={p.n} className="flex items-center gap-2 px-3 py-2 text-sm"><Avatar name={p.n} /><span className="flex-1 truncate" style={{ color: T.text }}>{p.n}</span><span className="text-[11px]" style={{ color: T.warning }}>{t("landing.demo.finance.overdue", { n: p.days })}</span><span className="font-medium tabular-nums" style={{ color: T.danger }}>{p.a} ₼</span></div>
                ))}
            </div>
        </div>
    );
}

const PERMS = ["students_view", "attendance_mark", "finance_view", "finance_edit", "reports_export", "roles_manage"] as const;
const ROLE_PERMS: Record<string, string[]> = {
    institution_owner: [...PERMS],
    branch_manager: ["students_view", "attendance_mark", "finance_view", "reports_export"],
    accountant: ["students_view", "finance_view", "finance_edit", "reports_export"],
    teacher: ["students_view", "attendance_mark"],
};

function RolesDemo() {
    const { t } = useTranslation();
    const [r, setR] = useState("teacher");
    const granted = ROLE_PERMS[r];
    return (
        <div className="space-y-3">
            <Tabs testid="demo-roles-tabs" value={r} onChange={setR} items={Object.keys(ROLE_PERMS).map((k) => ({ key: k, label: t(`role.${k}` as any) }))} />
            <div className="grid gap-1.5 sm:grid-cols-2">
                {PERMS.map((p) => {
                    const on = granted.includes(p);
                    return (
                        <div key={p} data-testid={`demo-perm-${p}`} className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-[background-color,border-color,opacity] duration-300"
                            style={{ borderColor: on ? T.accent : T.border, background: on ? T.primarySoft : T.card, opacity: on ? 1 : 0.6 }}>
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-md" style={{ background: on ? T.primary : T.border, color: "#fff" }}>{on ? <Check size={12} /> : <X size={12} />}</span>
                            <span style={{ color: T.text }}>{t(`landing.demo.perm.${p}` as any)}</span>
                        </div>
                    );
                })}
            </div>
            <div className="text-[11px]" style={{ color: T.textMuted }}>{t("landing.demo.roles.audit")}</div>
        </div>
    );
}

export type FeatureKey = "branches" | "groups" | "attendance" | "schedule" | "finance" | "roles";
export const FEATURES: { key: FeatureKey; Icon: LucideIcon; Demo: ComponentType }[] = [
    { key: "branches", Icon: Building2, Demo: BranchesDemo },
    { key: "groups", Icon: Layers3, Demo: GroupsDemo },
    { key: "attendance", Icon: ClipboardCheck, Demo: AttendanceDemo },
    { key: "schedule", Icon: CalendarDays, Demo: ScheduleDemo },
    { key: "finance", Icon: Wallet, Demo: FinanceDemo },
    { key: "roles", Icon: ShieldCheck, Demo: RolesDemo },
];
